import React, { useCallback, useEffect, useState } from "react";

import * as turf from "@turf/turf";

import { getDigifarmMapsGeo, getSoilAICropModelSummary, getSoilAICropPredictions } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import {
  AdminHead,
  AdminSection,
  AdminTable,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  InputNumber,
  MainPage,
  MapInteractive,
  Option,
  Row,
  Select,
  TagNormal,
  Message as message,
} from "components";

export const CropDetectionRD = () => {
  const [summary, setSummary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(2020);
  const [endYear, setEndyear] = useState(2022);
  const [totalYears, setTotalYears] = useState(1);
  const [groups, setGroups] = useState(false);
  const [geometries, setGeometries] = useState([]);
  const [mapGrid, setMapGrid] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [defaultZoom, setDefaultZoom] = useState(16);
  const [markers, setMarkers] = useState([]);
  const [location, setLocation] = useState({
    lat: 52.5224832,
    lng: 13.3906564,
  });

  const signal = useCancelAsyncOnUnmount()();

  const years = Array(10)
    .fill()
    .map((_, idx) => 2014 + idx);

  const loadSummary = () => {
    getSoilAICropModelSummary({
      signal,
    }).then((res) => {
      setSummary(res);
    });
  };

  const getGeometry = (center, item, previousGeos) => {
    setLoading(true);

    getDigifarmMapsGeo({
      lat: center.lat,
      lng: center.lng,
    }).then((res) => {
      const geos = previousGeos;

      if (res && res.length) {
        const turfPoly = turf.polygon([res.map(({ lat, lng }) => [lat, lng])]);

        const centroid = turf.centroid(turfPoly);

        let insert = true;

        previousGeos.forEach((field) => {
          field.geometry.forEach((geo, j) => {
            if (res[j] && geo.lng === res[j].lng && geo.lat === res[j].lat) {
              insert = false;
            }
          });
        });

        if (insert) {
          geos.push({
            geometry: res,
            field: {
              id: item,
            },
            center: {
              lat: centroid.geometry.coordinates[0],
              lng: centroid.geometry.coordinates[1],
            },
          });
        }
      }

      if (item < mapGrid.length - 1) {
        getGeometry(mapGrid[item + 1], item + 1, geos);
      } else {
        const loc = location;
        const fieldMarkers = geos.map((geo) => ({ location: geo.center, name: `Field ${geo.field.id}` }));

        setLocation(false);
        setMarkers([]);

        setTimeout(() => {
          setGeometries(geos);
          setLocation(loc);
          setMarkers(fieldMarkers);
          setLoading(false);
        }, 250);
      }
    });
  };

  const getPredictions = useCallback(
    (geometries, i, currentYear) => {
      getSoilAICropPredictions({
        field: geometries[i],
        year: currentYear,
        signal,
      })
        .then((res) => {
          const newPredictions = predictions;
          newPredictions[predictions.length] = res;
          setPredictions([...newPredictions, ...[false]]);

          // get next year for this geometry
          if (currentYear < year + totalYears) {
            getPredictions(geometries, i, currentYear + 1);
          } else if (geometries.length > i + 1) {
            // otherwise get next geometry
            getPredictions(geometries, i + 1, currentYear - totalYears);
          }
        })
        .catch((_err) => {
          getPredictions(geometries, i, currentYear);
          message({
            content: `There was a problem collecting data for field ${i}. Trying again...`,
            icon: "gremlin",
          });
        });
    },
    [signal, predictions, totalYears, year]
  );

  const generateGeometries = () => {
    setGeometries([]);
    setMarkers(mapGrid.map((g) => ({ location: g })));
    getGeometry(mapGrid[0], 0, []);
  };

  const createCenterpointGrid = (e) => {
    const { marginBounds } = e;

    const gridCount = 7;

    const divisions = {
      lng: (marginBounds.ne.lng - marginBounds.nw.lng) / gridCount,
      lat: (marginBounds.ne.lat - marginBounds.se.lat) / gridCount,
    };

    const grid = [];

    for (let i = 0; i < gridCount; i += 1) {
      for (let j = 0; j < gridCount; j += 1) {
        grid.push({
          lng: marginBounds.nw.lng + divisions.lng * i + divisions.lng * 0.5,
          lat: marginBounds.se.lat + divisions.lat * j + divisions.lat * 0.5,
        });
      }
    }

    setMapGrid(grid);
    setDefaultZoom(e.zoom);
  };

  const updateLat = (lat) => {
    const loc = location;
    setLocation(false);

    setTimeout(() => {
      setLocation({
        lat: parseFloat(lat.replace(",", ".")),
        lng: loc.lng,
      });
    }, 250);
  };

  const updateLong = (lng) => {
    const loc = location;
    setLocation(false);

    setTimeout(() => {
      setLocation({
        lat: loc.lat,
        lng: parseFloat(lng.replace(",", ".")),
      });
    }, 250);
  };

  useEffect(() => {
    setTotalYears(endYear - year);
  }, [year, endYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (geometries.length > 0) {
      setPredictions([false]);
      getPredictions(geometries, 0, year);
      loadSummary();
    }
  }, [geometries]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage pageName={"admin"}>
      <AdminHead
        headline={
          <>
            SoilAI crop detection for R&D <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description={
          <>
            Specific a long/lat of a region and years to retrieve predictions for. SoilAI will first identify fields within a 1km radius of that point, then get
            predictions for each field per year. <b>It may take some time for many fields/years.</b>
            <br />
            <br />
            {summary
              ? `The current accuracy of the crop prediction model is ${summary.accuracyTop1}% for the top prediction, and ${summary.accuracyTop3}% for the top 3 predictions. Models are retrained daily for improving accuracy. Results are presented as Crop (probability).`
              : ""}
          </>
        }
        extra={<></>}
      />
      <Row>
        <Col sm={6}>
          <Form name="longitude">
            <InputNumber
              name="longitude"
              label="Longitude"
              rules={[{ required: true }]}
              min={-100000}
              step={0.0001}
              onBlur={(v) => updateLong(v.target.value)}
              disabled={loading}
            />
          </Form>
        </Col>
        <Col sm={6}>
          <Form name="latitude">
            <InputNumber
              name="latitude"
              label="Latitude"
              rules={[{ required: true }]}
              min={-100000}
              step={0.0001}
              onBlur={(v) => updateLat(v.target.value)}
              disabled={loading}
            />
          </Form>
        </Col>
        <Col sm={12} />
      </Row>
      {location && location.lng && location.lat ? (
        <Row>
          <Col>
            <Card className="map-card" style={{ height: "50rem" }}>
              <MapInteractive
                defaultZoom={defaultZoom}
                geometries={geometries.map((geometry) => ({
                  ...geometry,
                  type: "shapeFile",
                }))}
                location={location}
                markers={markers}
                scaleControl={true}
                zoomControl={true}
                onChange={(e) => createCenterpointGrid(e)}
              />
            </Card>
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <Row justify="space-around" align="middle">
        <Col sm={6}>
          <Form name="year">
            <Select name="year" label="Starting year" onChange={(v) => setYear(v)} initialValue={year} style={{ textAlign: "left" }} disabled={loading}>
              {years.map((year, i) => (
                <Option key={i} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Form>
        </Col>
        <Col sm={6}>
          <Form name="endYear">
            <Select name="endYear" label="Ending year" onChange={(v) => setEndyear(v)} initialValue={endYear} style={{ textAlign: "left" }} disabled={loading}>
              {years.map((year, i) => (
                <Option key={i} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Form>
        </Col>
        <Col sm={4}>
          <Checkbox checked={groups} onChange={(e) => setGroups(e.target.checked)} disabled={loading}>
            Groups
          </Checkbox>
        </Col>
        <Col sm={8}>
          <Button type="primary" onClick={() => generateGeometries()} disabled={loading} loading={loading}>
            Do it!
          </Button>
        </Col>
      </Row>

      <AdminSection headline="Crop predictions" />
      {predictions.length && geometries.length ? (
        <Row>
          <Col sm={10}>
            <AdminTable
              className="no-action-table"
              dataSource={predictions.map((field, i) => {
                if (field.field && field.data) {
                  return {
                    field: `Field ${i}`,
                    [`${Object.keys(geometries[0].field)[0]}`]: `${Object.values(field.field)[0]}`,
                    year: field.year,
                  };
                }
                return {
                  field: `Field ${i}`,
                  [`${Object.keys(geometries[0].field)[0]}`]: "...",
                  year: "",
                };
              })}
              config={{
                exclude: ["field"],
              }}
            />
          </Col>
          <Col sm={14}>
            <AdminTable
              className="no-action-table"
              dataSource={predictions.map((field, i) => {
                if (field.field && field.data) {
                  const data = field.data.crops;
                  const predictions = groups ? data.groups : data.predictions;

                  return {
                    ...{
                      "Prediction 1": `${Object.keys(predictions)[0]} (${(Object.values(predictions)[0] * 100).toFixed(2)}%)`,
                      "Prediction 2": `${Object.keys(predictions)[1]} (${(Object.values(predictions)[1] * 100).toFixed(2)}%)`,
                      "Prediction 3": `${Object.keys(predictions)[2]} (${(Object.values(predictions)[2] * 100).toFixed(2)}%)`,
                    },
                  };
                }

                return {
                  ...{
                    "Prediction 1": `Retrieving ${i + 1}/${geometries.length * (totalYears + 1)}...`,
                    "Prediction 2": "",
                    "Prediction 3": "",
                  },
                };
              })}
              config={{
                exclude: ["field"],
                actions: null,
              }}
            />
          </Col>
        </Row>
      ) : (
        <></>
      )}
    </MainPage>
  );
};
